import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
// ⚠️ Deep import of the .desktop variant is REQUIRED (ledger hazard 16): CASH_REGISTER_MODAL_TYPES
// is DIVERGENT (web 4 keys, mobile 3), and a bare folder import types it against index.native —
// which has no `close_switch_user`. CASH_SETTLEMENT_STATUS_TYPES comes along from ./shared.
import {
  CASH_REGISTER_MODAL_TYPES,
  CASH_SETTLEMENT_STATUS_TYPES,
} from "../../../utils/constants";
// ⚠️ Deep import of the .desktop variant is REQUIRED (ledger hazard 6): a bare folder import
// typechecks against index.native, whose LoginSlice exports a different surface.
import { supportLogout, userLogout } from "../LoginSlice";
import {
  sharedInitialState,
  sharedReducers,
  type SharedCashRegisterState,
} from "./shared";

interface CashRegisterState extends SharedCashRegisterState {
  cashSettlementDetails: Record<any, any>;
  branchSwitchData: Record<string, string>;
  switchUserData: Record<string, string>;
  errorModalConfig: Record<string, string>;
}

const initialState: CashRegisterState = {
  ...sharedInitialState,
  cashSettlementDetails: {},
  branchSwitchData: {},
  switchUserData: {},
  errorModalConfig: {},
};

export const CashRegisterSlice = createSlice({
  name: "cashRegister",
  initialState,
  reducers: {
    ...sharedReducers,
    // Diverges from mobile: web also stores branchSwitchData/switchUserData, and its `if` has
    // two extra branches (`close_switch_branch`, and `close_switch_user` + status `open`).
    saveCashSettlementDetails: (
      state: CashRegisterState,
      actions: PayloadAction<any>
    ) => {
      state.cashSettlementDetails = actions.payload.response || {};
      state.branchSwitchData = actions.payload.branchSwitchData;
      state.switchUserData = actions.payload.switchUserData;
      if (
        (actions?.payload?.modalType ===
          CASH_REGISTER_MODAL_TYPES.closing_balance &&
          actions?.payload?.response?.status ===
            CASH_SETTLEMENT_STATUS_TYPES.open) ||
        (actions?.payload?.modalType ===
          CASH_REGISTER_MODAL_TYPES.opening_balance &&
          actions?.payload?.response?.status ===
            CASH_SETTLEMENT_STATUS_TYPES.draft) ||
        actions?.payload?.modalType ===
          CASH_REGISTER_MODAL_TYPES.close_switch_branch ||
        (actions?.payload?.modalType ===
          CASH_REGISTER_MODAL_TYPES.close_switch_user &&
          actions?.payload?.response?.status ===
            CASH_SETTLEMENT_STATUS_TYPES.open)
      ) {
        state.cashRegisterModalType = actions?.payload?.modalType;
        state.showCashRegisterModal = true;
      }
    },
    updateCashRegisterSlice: (
      state: CashRegisterState,
      action: PayloadAction<any>
    ) => {
      return action.payload;
    },
    updateSettlementIdDetails: (
      state: CashRegisterState,
      action: PayloadAction<any>
    ) => {
      state.cashSettlementDetails.mid = action.payload;
    },
    updateErrorModalConfig: (
      state: CashRegisterState,
      action: PayloadAction<any>
    ) => {
      state.errorModalConfig = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ⚠️ BOTH CASES ARE EMPTY ON PURPOSE — verbatim from apps/web. The cases ARE registered
    // (so the actions produce a new draft) but neither body does anything. Unlike most web
    // slices, CashRegisterSlice does NOT reset on logout. Preserved exactly.
    builder
      .addCase(userLogout, (state) => {})
      .addCase(supportLogout, (state) => {});
  },
});

export const {
  openCashRegister,
  closeCashRegister,
  getCashSettlementDetails,
  saveCashSettlementDetails,
  updateSettlementIdDetails,
  closeCashRegisterModal,
  updateCashRegisterSlice,
  updateErrorModalConfig,
} = CashRegisterSlice.actions;

export default CashRegisterSlice.reducer;
